import type {Point, Rectangle} from 'pixi.js';
import type {Signal, ComputedSignal} from '@gongxigames/framework/core/fragments/store/Store';
import type {Override} from '@gongxigames/framework/lib/types/basic';
import type {JSX} from './jsx-runtime';

export type PointLike = Point | {x: number; y: number;} | [number, number] | number;
export type RectangleLike = Rectangle | [width: number, height: number] | [x: number, y: number, width: number, height: number] | number;

/**
 * Converts a type into a set of types
 * consisting of the original type
 * and signals containing the original type.
 * Сan be useful when we describe types for components.
 */
export type CanBeSignal<T> = T | Signal<T> | ComputedSignal<T>;

/**
 * Converts a `Record<string, T>` into a record of possible signals.
 * `Record<string, T>` -> `Record<string, CanBeSignal<T>>`
 * Checkout `CanBeSignal` for more information.
 * Сan be useful when we describe types for components.
 */
export type CanBeSignals<T> = {
    [K in keyof T]: CanBeSignal<T[K]>
};

export type GetComponentPropType<T> = T extends Signal<any> ? never : T;
export type CleanComponentProps<T> = {[K in keyof T]: GetComponentPropType<T[K]>};

export type ExtendProps<T, PARENT, REF = null> = REF extends null ?
    CanBeSignals<T> & PARENT:
    Override<CanBeSignals<T> & PARENT, SpecialProps<REF>>;

export type ComponentRef<T> = Signal<T> | ((ref: T) => void);

export type SpecialProps<T> = {
    instance?: T;
    ref?: ComponentRef<T>;
};

export type EventCallbacks<T> = T | T[];

export type EventMap = {
    [K: string]: EventCallbacks<(...args: any[]) => void>;
};

export type ConstructorCallBack = () => JSX.Element;
